// Full finding detail view with review form (status/severity/notes/PoC).

import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { AttackPlaybookTeaser, RiskAnalysisTeaser, type TeaserReason } from "@/components/ui/AITeaser";
import { SeverityBadge } from "@/components/ui/SeverityBadge";
import {
  useEngagementAssets,
  useUpdateFinding,
} from "@/hooks/useEngagementDetail";
import { usePlan } from "@/hooks/usePlan";
import { toast } from "@/stores/toastStore";
import { useDict } from "@/i18n/LocaleProvider";
import type { Finding, FindingStatus } from "@/types/engagement-detail";

interface Props {
  engagementId: string;
  finding: Finding;
  onBack: () => void;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  if (!children) return null;
  return (
    <div className="mb-4">
      <div className="font-mono text-[10px] text-muted uppercase tracking-[0.08em] mb-[6px]">
        {title}
      </div>
      <div className="font-sans text-[13px] text-content leading-[1.7] whitespace-pre-wrap">
        {children}
      </div>
    </div>
  );
}

const STATUS_VALUES: FindingStatus[] = ["needs_review", "confirmed", "false_positive", "needs_retest", "informational"];

const SEVERITIES = ["critical", "high", "medium", "low", "informational"] as const;

export function FindingDetailDialog({ engagementId, finding, onBack }: Props) {
  const navigate = useNavigate();
  const d = useDict();
  const td = d.app.findingDetail;
  const sevMap = d.app.severities;
  const statusMap = d.app.findings.statuses;
  const [status, setStatus] = useState<string>(finding.status);
  const [severity, setSeverity] = useState<string>(
    finding.severity_final || finding.severity_suggested || "medium",
  );
  const [notes, setNotes] = useState(finding.reviewer_notes ?? "");
  const [poc, setPoc] = useState(finding.proof_of_concept ?? "");

  const plan = usePlan();
  const hasAI = plan.hasFeature("ai_enrichment");

  // Cross-reference the asset's ownership status — AI also needs the
  // engagement's domain ownership confirmed (Phase 8.0d).
  const assets = useEngagementAssets(engagementId);
  const findingAsset = assets.data?.find((a) => a.id === finding.asset_id);
  const ownershipVerified = findingAsset?.ownership_verified ?? false;

  // Three independent gates between a finding and its AI fields:
  //   1. user's plan includes ai_enrichment       (hasAI)
  //   2. the finding's asset is ownership-verified (ownershipVerified)
  //   3. the orchestrator actually ran enrichment   (finding.ai_enriched)
  //
  // When (1) and (2) pass but (3) is still false, the teaser used to
  // fall back to "Upgrade to Pro" — misleading because the user is
  // already on the plan. Now we surface the real cause via a third
  // "pending" reason that shows a non-locking refresh prompt instead.
  const qc = useQueryClient();
  const teaserReason: TeaserReason = !hasAI
    ? "plan"
    : !ownershipVerified
      ? "ownership"
      : "pending";

  const refreshFinding = () => {
    qc.invalidateQueries({ queryKey: ["engagement", engagementId] });
  };

  // Single source of truth for what each teaser CTA does. Both
  // RiskAnalysisTeaser and AttackPlaybookTeaser route through this so
  // they stay in sync.
  const onTeaserAction = () => {
    if (teaserReason === "ownership") {
      toast.info(
        td.ownershipToast,
      );
    } else if (teaserReason === "pending") {
      refreshFinding();
    } else {
      navigate("/app/upgrade");
    }
  };

  const update = useUpdateFinding(engagementId);

  async function handleSave() {
    try {
      await update.mutateAsync({
        findingId: finding.id,
        status,
        severity_final: severity,
        reviewer_notes: notes,
        proof_of_concept: poc,
      });
      toast.success(td.updated);
      onBack();
    } catch (e) {
      toast.error(`${td.saveFailed} ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  const currentSeverity = (finding.severity_final ||
    finding.severity_suggested ||
    "informational") as string;

  return (
    <div>
      <Button variant="ghost" small onClick={onBack} className="mb-3">
        {td.back}
      </Button>

      <Card>
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <SeverityBadge severity={currentSeverity} />
          <span className="font-display text-[18px] font-semibold text-content flex-1">
            <span className="font-mono text-[13px] text-dim mr-2">
              #{finding.display_number ?? "—"}
            </span>
            {finding.title}
          </span>
          {finding.ai_enriched && (
            <Badge color="rgb(var(--color-accent-bright))" bg="rgb(var(--color-accent-bg))">
              {td.aiEnriched}
            </Badge>
          )}
        </div>

        <Section title={td.descr}>{finding.description_draft}</Section>
        <Section title={td.impact}>{finding.impact_draft}</Section>
        <Section title={td.remediation}>{finding.remediation_draft}</Section>

        {finding.cvss_vector && (
          <div className="mb-4 p-3 bg-bg2 rounded border border-stroke">
            <div className="font-mono text-[10px] text-accent uppercase tracking-[0.08em] mb-2">
              CVSS v3.1
            </div>
            <div className="flex gap-4 items-center">
              <div
                className="font-display text-[24px] font-bold"
                style={{
                  color:
                    (finding.cvss_score ?? 0) >= 7
                      ? "rgb(var(--color-danger))"
                      : (finding.cvss_score ?? 0) >= 4
                        ? "rgb(var(--color-warning))"
                        : "rgb(var(--color-success))",
                }}
              >
                {(finding.cvss_score ?? 0).toFixed(1)}
              </div>
              <div className="font-mono text-[11px] text-muted">
                {finding.cvss_vector}
              </div>
            </div>
          </div>
        )}

        {finding.risk_score != null ? (
          <div className="mb-4 p-3 bg-bg2 rounded border border-stroke">
            <div className="font-mono text-[10px] text-accent uppercase tracking-[0.08em] mb-2">
              {td.riskAnalysis}
            </div>
            <div className="flex gap-4 items-center">
              <div
                className="font-display text-[28px] font-bold"
                style={{
                  color:
                    finding.risk_score >= 70
                      ? "rgb(var(--color-danger))"
                      : finding.risk_score >= 40
                        ? "rgb(var(--color-warning))"
                        : "rgb(var(--color-success))",
                }}
              >
                {finding.risk_score}/100
              </div>
              <div>
                <div className="font-mono text-[11px] text-content font-semibold">
                  {td.level} {finding.risk_level ?? "?"}
                </div>
                <div className="font-mono text-[11px] text-muted">
                  {td.priority} {finding.priority_rank ?? "?"}
                </div>
              </div>
            </div>
            {finding.risk_factors && finding.risk_factors.length > 0 && (
              <div className="mt-2">
                <div className="font-mono text-[10px] text-muted mb-1">
                  {td.factors}
                </div>
                {finding.risk_factors.map((f, i) => (
                  <div
                    key={i}
                    className="font-sans text-[12px] text-content pl-3"
                  >
                    • {f}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <RiskAnalysisTeaser reason={teaserReason} onAction={onTeaserAction} />
        )}

        {finding.attack_playbook ? (
          <div
            className="mb-4 p-3 bg-bg2 rounded"
            style={{ border: "1px solid rgb(var(--color-danger) / 0.376)" }}
          >
            <div className="font-mono text-[10px] text-danger uppercase tracking-[0.08em] mb-2">
              {td.attackPlaybook}
            </div>
            {/* v2 shape: applicable=false → just say so + reason, hide the chain. */}
            {finding.attack_playbook.applicable === false ? (
              <div className="font-sans text-[12px] text-muted italic">
                {td.notApplicable}
                {finding.attack_playbook.skip_reason && (
                  <span className="text-content"> — {finding.attack_playbook.skip_reason}</span>
                )}
              </div>
            ) : (
              <>
                {/* v2: preconditions */}
                {finding.attack_playbook.preconditions && finding.attack_playbook.preconditions.length > 0 && (
                  <div className="mb-2">
                    <div className="font-mono text-[10px] text-muted mb-1">{td.preconditions}</div>
                    {finding.attack_playbook.preconditions.map((p, i) => (
                      <div key={i} className="font-sans text-[12px] text-content pl-3">• {p}</div>
                    ))}
                  </div>
                )}
                {/* v2: structured attack chain */}
                {finding.attack_playbook.attack_chain && finding.attack_playbook.attack_chain.length > 0 && (
                  <div className="mb-2">
                    <div className="font-mono text-[10px] text-muted mb-1">{td.attackChain}</div>
                    {finding.attack_playbook.attack_chain.map((s, i) => (
                      <div key={i} className="font-sans text-[12px] text-content pl-3 mb-1">
                        <span className="text-accent font-semibold">{s.step ?? i + 1}.</span>{" "}
                        {s.action}
                        {s.expected_result && (
                          <div className="text-muted text-[11px] pl-4">→ {s.expected_result}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* v2: what attacker gains */}
                {finding.attack_playbook.what_attacker_gains && (
                  <div className="font-sans text-[12px] text-content mb-2">
                    <span className="text-accent font-semibold">{td.attackerGains}</span>
                    {finding.attack_playbook.what_attacker_gains}
                  </div>
                )}
                {/* v2: detection signals */}
                {finding.attack_playbook.detection_signals && finding.attack_playbook.detection_signals.length > 0 && (
                  <div className="mb-2">
                    <div className="font-mono text-[10px] text-muted mb-1">{td.detectionSignals}</div>
                    {finding.attack_playbook.detection_signals.map((d, i) => (
                      <div key={i} className="font-sans text-[12px] text-content pl-3">• {d}</div>
                    ))}
                  </div>
                )}
                {/* v1 legacy fields — render only when v2 fields absent
                    so old AI-enriched findings stay readable. */}
                {!finding.attack_playbook.attack_chain && finding.attack_playbook.objective && (
                  <div className="font-sans text-[12px] text-content mb-2">
                    <span className="text-accent font-semibold">{td.objective}</span>
                    {finding.attack_playbook.objective}
                  </div>
                )}
                {!finding.attack_playbook.attack_chain && finding.attack_playbook.attack_path && (
                  <div className="mb-2">
                    <div className="font-mono text-[10px] text-muted mb-1">{td.attackPath}</div>
                    {finding.attack_playbook.attack_path.map((s, i) => (
                      <div key={i} className="font-sans text-[12px] text-content pl-3">{i + 1}. {s}</div>
                    ))}
                  </div>
                )}
                {!finding.attack_playbook.attack_chain && finding.attack_playbook.realistic_scenario && (
                  <div
                    className="font-sans text-[12px] text-muted italic mt-2 pl-3"
                    style={{ borderLeft: "2px solid rgb(var(--color-accent))" }}
                  >
                    {finding.attack_playbook.realistic_scenario}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <AttackPlaybookTeaser reason={teaserReason} onAction={onTeaserAction} />
        )}

        {finding.proof_of_concept && (
          <div className="mb-4 p-3 bg-bg rounded border border-stroke">
            <div className="font-mono text-[10px] text-success uppercase tracking-[0.08em] mb-2">
              {td.poc}
            </div>
            <pre className="font-mono text-[11px] text-content whitespace-pre-wrap leading-[1.6]">
              {finding.proof_of_concept}
            </pre>
          </div>
        )}

        <div className="border-t border-stroke mt-5 pt-5">
          <div className="font-mono text-[11px] text-accent uppercase tracking-[0.08em] mb-4">
            {td.review}
          </div>
          {/* Stack selects on mobile (two side-by-side dropdowns get
              cramped at 320-375px). text-base on mobile prevents iOS
              Safari from auto-zooming the viewport when a select gains
              focus — same trick used on the Input component. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block mb-[6px] text-[11px] text-muted font-mono">
                {td.status}
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-2 py-[10px] sm:py-2 bg-bg border border-stroke rounded text-content font-mono text-base sm:text-[12px]"
              >
                {STATUS_VALUES.map((sv) => (
                  <option key={sv} value={sv}>
                    {statusMap[sv as keyof typeof statusMap] ?? sv}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block mb-[6px] text-[11px] text-muted font-mono">
                {td.severityOverride}
              </label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="w-full px-2 py-[10px] sm:py-2 bg-bg border border-stroke rounded text-content font-mono text-base sm:text-[12px]"
              >
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {sevMap[s as keyof typeof sevMap] ?? s}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <Input
            label={td.reviewerNotes}
            value={notes}
            onChange={setNotes}
            placeholder={td.reviewerNotesPh}
            textarea
            className="mt-3"
          />
          <Input
            label={td.pocLabel}
            value={poc}
            onChange={setPoc}
            placeholder={td.pocPh}
            textarea
          />
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={update.isPending}
          >
            {update.isPending ? td.saving : td.saveReview}
          </Button>
        </div>
      </Card>
    </div>
  );
}
